import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatDate } from "@/lib/utils";
import type { OrderStatus } from "@/services/admin-order.service";
import {
  ALL_STATUSES,
  ORDER_STATUS_OPTIONS,
  useOrderFiltersStore,
} from "@/stores/orderFiltersStore";
import { Filter, Loader2, Plus, Search, X } from "lucide-react";

const SEARCH_DEBOUNCE_MS = 750;

interface OrderFiltersProps {
  onProvisionVm?: () => void;
}

/**
 * Filter bar for the admin order list — the shared list-screen toolbar: one 360px search box, the
 * screen's own action beside it, the active-filter chips, then the Filters popover.
 *
 * The separate "tenant ID" box that used to sit next to the search is gone. The backend now routes
 * the one term by its SHAPE (`OrderRepository.GetOrdersForAdminAsync`), so a pasted GUID is matched
 * by equality against the order, its tenant AND the user who placed it. The second box also carried
 * a hack the routing retires: a non-GUID typed into it was rewritten to Guid.Empty by the page so
 * the backend would answer nothing, which is a strange way to say "that is not an id".
 */
export function OrderFilters({ onProvisionVm }: OrderFiltersProps) {
  const { keyword, status, dateRange } = useOrderFiltersStore(
    (state) => state.filters,
  );
  const setFilter = useOrderFiltersStore((state) => state.setFilter);

  // Only the half-typed term is local: committing per keystroke would re-query and write
  // sessionStorage per character.
  const [keywordInput, setKeywordInput] = useState(keyword || "");

  useEffect(() => {
    setKeywordInput(keyword || "");
  }, [keyword]);

  useEffect(() => {
    const trimmed = (keywordInput || "").trim();
    const timer = window.setTimeout(() => {
      if (trimmed !== (keyword || "")) {
        setFilter("keyword", trimmed);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [keywordInput, keyword, setFilter]);

  // A term typed but not yet committed: the debounce has not fired, so nothing is fetching and the
  // table still shows the previous result. The spinner in the box reads from this.
  const isSearchPending = (keywordInput || "").trim() !== (keyword || "");

  const currentStatusLabel = ORDER_STATUS_OPTIONS.find(
    (o) => o.value === status,
  )?.label;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
      <div className="w-full sm:w-[360px]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          {/* The debounce window has no isFetching to report, so the box says so itself. */}
          {isSearchPending && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
          <Input
            placeholder="Search order no., ID, email, phone"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            // React withholds onChange while an IME composition is open — which is the whole time a
            // Vietnamese Telex/VNI method is assembling a word — so the term only reached state
            // once a space closed the composition, and the search appeared to need a trailing
            // space to run at all.
            onCompositionEnd={(e) => setKeywordInput(e.currentTarget.value)}
            className="pl-10 pr-9"
          />
        </div>
      </div>

      {/* Sits directly after the search box rather than at the far end of the toolbar: it is the
          only control here that creates something. */}
      {onProvisionVm && (
        <Button
          onClick={onProvisionVm}
          variant="outline"
          size="sm"
          className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm hover:translate-y-0 active:translate-y-0"
        >
          <Plus className="h-4 w-4" />
          Provision VM for user
        </Button>
      )}

      <div className="order-3 flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:self-center sm:justify-end">
        {keyword && (
          <Badge
            variant="outline"
            className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
          >
            Search: {keyword}
            <button
              type="button"
              onClick={() => {
                setKeywordInput("");
                setFilter("keyword", "");
              }}
              aria-label="Clear search filter"
              className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {status !== ALL_STATUSES && (
          <Badge
            variant="outline"
            className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
          >
            Status: {currentStatusLabel || status}
            <button
              type="button"
              onClick={() => setFilter("status", ALL_STATUSES)}
              aria-label="Clear status filter"
              className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {dateRange.from && dateRange.to && (
          <Badge
            variant="outline"
            className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
          >
            Placed: {formatDate(dateRange.from)} – {formatDate(dateRange.to)}
            <button
              type="button"
              onClick={() => setFilter("dateRange", {})}
              aria-label="Clear date filter"
              className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
      </div>

      <Popover>
        {/* Icon only, so the accessible name has to come from somewhere: `title` gives the hover
            tooltip, `aria-label` gives screen readers the same words. The comment sits outside
            PopoverTrigger on purpose: `asChild` forwards to a single child. */}
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            title="Filters"
            aria-label="Filters"
            className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
          >
            <Filter className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="orderStatusFilter">Status</Label>
            <Select
              value={status}
              onValueChange={(value) =>
                setFilter("status", value as OrderStatus | typeof ALL_STATUSES)
              }
            >
              <SelectTrigger id="orderStatusFilter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                {ORDER_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="orderPlacedDateFilter">Placed date</Label>
            <DateRangePicker
              value={dateRange}
              onChange={(range) => setFilter("dateRange", range)}
              placeholder="Select date range"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
